import styled from "@emotion/styled";
import { FilterValues, FilterProps, SupportedKmsTypeFilter, KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { Link } from "@kaltura/ds-react-components";
import { translate, extractDefaultFilterValues, getComponentFromPath } from "@kaltura/mediaspace-shared-utils";
import { FilterWrapper } from "./FilterWrapper";
import { Filter } from "./filter/Filter";
import { getDisabledFilters } from "./dependencyUtils";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useMemo, useState, useRef, useEffect } from "react";
import { isEqual } from "lodash";
import { ErrorBoundary, SpanSrOnly } from "@kaltura/mediaspace-shared-ui";

export interface VerticalFilterBarProps {
    filters: FilterProps<SupportedKmsTypeFilter>[];
    values: FilterValues;
    onChange: (values: FilterValues) => void;
    onClear?: () => void;
    noClearAll?: boolean;
    customTopFilterComponents?: KmsTypeComponent[];
}

const StyledButton = styled(Link)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

/**
 * vertical filter bar - holds all the filters, and manage their state
 */
export function VerticalFilterBar(props: VerticalFilterBarProps) {
    const sendButtonAnalytics = useButtonAnalytics();
    const {
        filters: initialFilters = [],
        values: propValues = {},
        onChange,
        onClear,
        noClearAll,
        customTopFilterComponents = [],
    } = props;

    const values = { keyword: "", ...propValues };
    const filters = getDisabledFilters(values, initialFilters);

    const [filterAnnouncement, setFilterAnnouncement] = useState("");
    const announcementTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Clean up timeout on unmount
    useEffect(() => {
        return () => {
            if (announcementTimeoutRef.current !== null) {
                clearTimeout(announcementTimeoutRef.current);
            }
        };
    }, []);

    const announceToScreenReader = (message: string) => {
        // Clear any pending announcement timeout
        if (announcementTimeoutRef.current !== null) {
            clearTimeout(announcementTimeoutRef.current);
        }

        setFilterAnnouncement("");
        // Use setTimeout to ensure the clear happens before the new announcement
        announcementTimeoutRef.current = setTimeout(
            () => {
                setFilterAnnouncement(message);
                announcementTimeoutRef.current = null;
            },
            10
        );
    };

    /**
     * handle a single filter value change
     *
     * we expect the filterValues to be relevant for a specific filter params
     * (filter main param, its group items params, free text items params)
     */
    const handleChange = (filterValues: FilterValues) => {
        // new values for filter
        let currentFilterValues: FilterValues = { ...values, ...filterValues };

        // update filter dependency by new values
        const disabledFilters = getDisabledFilters(currentFilterValues, initialFilters);

        // turn off values of disabled filters
        // (group item values are reset by the filters)
        disabledFilters.forEach((filter) => {
            if (filter.disabled) {
                currentFilterValues = { ...currentFilterValues, [filter.param]: null } as FilterValues;
            }
        });

        // Check if a default value was applied (when user unchecks last checkbox)
        const changedParam = Object.keys(filterValues)[0];
        const changedFilter = initialFilters.find((f) => f.param === changedParam);

        if (changedFilter && changedFilter.defaultValue != null) {
            const newValue = currentFilterValues[changedParam];
            const isArray = Array.isArray(newValue);

            // Single default: array with one element that is the default value (user unchecked all others, leaving only default selected)
            const isSingleDefault = isArray && newValue.length === 1 && newValue[0] === changedFilter.defaultValue;

            if (isSingleDefault) {
                const filterTitle = changedFilter.title || changedParam;
                const defaultItem = changedFilter.items?.find((item) => item.value === changedFilter.defaultValue);
                const defaultLabel = defaultItem?.label || changedFilter.defaultValue;

                announceToScreenReader(translate("Filter %1 reset to default value: %2", [filterTitle, defaultLabel]));
            }
        }

        onChange(currentFilterValues);
    };

    /**
     * default filters values
     */
    const defaultValues = useMemo(() => {
        const extractedDefaultValues = extractDefaultFilterValues(initialFilters);
        return { ...extractedDefaultValues, keyword: "" };
    }, [initialFilters]);

    /**
     * handle clear all filter values
     */
    const handleClearAll = () => {
        sendButtonAnalytics("Filters - Clear all filters", ButtonClickAnalyticsType.FILTER);
        announceToScreenReader(translate("All filters have been reset to default values"));
        // if onClear callback is provided, use it; otherwise fall back to onChange
        if (onClear) {
            onClear();
        } else {
            onChange(defaultValues);
        }
    };

    return (
        <>
            {/* Screen reader announcements for filter changes */}
            <SpanSrOnly role="status" aria-live="polite" aria-atomic="true">
                {filterAnnouncement}
            </SpanSrOnly>
            {!noClearAll && (
                <StyledButton
                    onClick={handleClearAll}
                    disabled={isEqual(defaultValues, values)}
                    className="kms-ds-filter-bar-clear-all-filters-button"
                    component={"button"}
                    size={"small"}
                >
                    {translate("Clear all filters")}
                </StyledButton>
            )}
            {customTopFilterComponents.map((topComponent, index) => {
                const { component, props } = topComponent;
                const Component = getComponentFromPath(component);
                return (
                    <ErrorBoundary key={index} fallback={<></>}>
                        <Component key={index} {...props} filterValues={values ?? {}} onChange={handleChange} />
                    </ErrorBoundary>
                );
            })}
            {filters
                .filter((filter) => !filter.hidden)
                .map((filter, index) => (
                    <FilterWrapper
                        title={filter.title}
                        param={filter.param}
                        disabled={filter.disabled}
                        key={index + filter.param}
                    >
                        <Filter {...filter} values={values ?? {}} onChange={handleChange} />
                    </FilterWrapper>
                ))}
        </>
    );
}

export default VerticalFilterBar;
